import React from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const STATUS_COLORS = {
  completed: '#5eb767',
  canceled: '#f16a5b',
  upcoming: '#f6a324',
  confirmed: '#4f9dee',
  unconfirmed: '#f8cc56',
  waiting: '#93a2ba'
};

const DEFAULT_COLOR = '#9aabc3';

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const { name, value, color } = payload[0];

  return (
    <div style={{
      background: 'rgba(17, 28, 48, 0.94)',
      border: '1px solid rgba(110, 137, 178, 0.45)',
      borderRadius: 10,
      padding: '9px 12px',
      color: '#d8e4f8',
      fontSize: 12
    }}>
      <strong style={{ color }}>{name}</strong>: {value}
    </div>
  );
};

export default class AppointmentStatusPieChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { datas: [], loading: true };
  }

  componentDidMount() {
    this.fetchData();
  }

  // PE-438: the statistics page appends its filter query; without the prop the
  // dashboard keeps fetching the bare endpoint exactly as before.
  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    // Rapid filter changes can resolve out of order; only the latest request
    // may write state. Inert on the dashboard, where query never changes.
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true });
    fetch('/api/appointments/status_chart' + (this.props.query || ''))
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(datas => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ datas, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ loading: false });
      });
  }

  render() {
    const { datas, loading } = this.state;

    if (!loading && (!datas || datas.length === 0)) {
      return <div className="text-center text-muted py-5">No data available</div>;
    }

    const chartData = loading ? [] : datas.map(item => ({
      name: item.label,
      value: item.value,
      fill: STATUS_COLORS[item.id] || DEFAULT_COLOR
    }));

    return (
      <div>
        {loading ? (
          <div style={{ height: 320, padding: '12px 0' }}>
            <Skeleton height={280} borderRadius={14} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} margin={{ top: 18, right: 6, left: -14, bottom: 2 }}>
              <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="#e7eef8" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#7f8ea8', fontWeight: 600 }}
                interval={0}
                height={40}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#8a98b0' }}
                width={30}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(50, 79, 124, 0.08)' }} />
              <Bar dataKey="value" radius={[10, 10, 0, 0]} maxBarSize={46}>
                {chartData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}
